// src/reducers/userReducer.js

import {
  // Auth/Profile
  REGISTER_USER_REQUEST,
  REGISTER_USER_SUCCESS,
  REGISTER_USER_FAIL,
  LOGIN_USER_REQUEST,
  LOGIN_USER_SUCCESS,
  LOGIN_USER_FAIL,
  LOGOUT_USER_REQUEST,
  LOGOUT_USER_SUCCESS,
  LOGOUT_USER_FAIL,
  FORGOT_PASSWORD_REQUEST,
  FORGOT_PASSWORD_SUCCESS,
  FORGOT_PASSWORD_FAIL,
  RESET_PASSWORD_REQUEST,
  RESET_PASSWORD_SUCCESS,
  RESET_PASSWORD_FAIL,
  LOAD_USER_REQUEST,
  LOAD_USER_SUCCESS,
  LOAD_USER_FAIL,
  UPDATE_PROFILE_REQUEST,
  UPDATE_PROFILE_SUCCESS,
  UPDATE_PROFILE_FAIL,
  UPDATE_PASSWORD_REQUEST,
  UPDATE_PASSWORD_SUCCESS,
  UPDATE_PASSWORD_FAIL,
  GET_USER_ACTIVITY_REQUEST,
  GET_USER_ACTIVITY_SUCCESS,
  GET_USER_ACTIVITY_FAIL,
  // User management (admin/franchise)
  ALL_USERS_REQUEST,
  ALL_USERS_SUCCESS,
  ALL_USERS_FAIL,
  USERS_BY_FRANCHISE_REQUEST,
  USERS_BY_FRANCHISE_SUCCESS,
  USERS_BY_FRANCHISE_FAIL,
  CREATE_USER_REQUEST,
  CREATE_USER_SUCCESS,
  CREATE_USER_FAIL,
  EDIT_USER_REQUEST,
  EDIT_USER_SUCCESS,
  EDIT_USER_FAIL,
  DELETE_USER_REQUEST,
  DELETE_USER_SUCCESS,
  DELETE_USER_FAIL,
  CHANGE_USER_ROLE_REQUEST,
  CHANGE_USER_ROLE_SUCCESS,
  CHANGE_USER_ROLE_FAIL,
  TOGGLE_USER_STATUS_REQUEST,
  TOGGLE_USER_STATUS_SUCCESS,
  TOGGLE_USER_STATUS_FAIL,
  CLEAR_ERRORS,
} from "../constants/userConstants";

// --- Auth/Profile/User state ---
const initialUserState = {
  user: null,
  isAuthenticated: false,
  isUserLoaded: false,
  loading: false,
  error: null,
  message: null,
  isUpdated: false,
  isPasswordUpdated: false,
  activity: [],
};

export const userReducer = (state = initialUserState, action) => {
  switch (action.type) {
    // Register/Login/Load
    case REGISTER_USER_REQUEST:
    case LOGIN_USER_REQUEST:
    case LOAD_USER_REQUEST:
      return { ...state, loading: true, error: null };

    case REGISTER_USER_SUCCESS:
    case LOGIN_USER_SUCCESS:
    case LOAD_USER_SUCCESS:
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        user: action.payload,
        isUserLoaded: true,
        error: null,
      };

    case REGISTER_USER_FAIL:
    case LOGIN_USER_FAIL:
    case LOAD_USER_FAIL:
      return {
        ...state,
        loading: false,
        isAuthenticated: false,
        user: null,
        error: action.payload,
      };

    // Logout
    case LOGOUT_USER_REQUEST:
      return { ...state, loading: true };
    case LOGOUT_USER_SUCCESS:
      return { ...initialUserState };
    case LOGOUT_USER_FAIL:
      return { ...state, loading: false, error: action.payload };

    // Forgot/Reset Password
    case FORGOT_PASSWORD_REQUEST:
    case RESET_PASSWORD_REQUEST:
      return { ...state, loading: true, error: null, message: null };

    case FORGOT_PASSWORD_SUCCESS:
    case RESET_PASSWORD_SUCCESS:
      return { ...state, loading: false, message: action.payload };

    case FORGOT_PASSWORD_FAIL:
    case RESET_PASSWORD_FAIL:
      return { ...state, loading: false, error: action.payload };

    // Update Profile/Password
    case UPDATE_PROFILE_REQUEST:
    case UPDATE_PASSWORD_REQUEST:
      return {
        ...state,
        loading: true,
        isUpdated: false,
        isPasswordUpdated: false,
        error: null,
      };

    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        loading: false,
        isUpdated: true,
        message: "Profile updated successfully.",
      };
    case UPDATE_PASSWORD_SUCCESS:
      return {
        ...state,
        loading: false,
        isPasswordUpdated: true,
        message: "Password updated successfully.",
      };

    case UPDATE_PROFILE_FAIL:
    case UPDATE_PASSWORD_FAIL:
      return { ...state, loading: false, error: action.payload };

    // Activity
    case GET_USER_ACTIVITY_REQUEST:
      return { ...state, loading: true, error: null };
    case GET_USER_ACTIVITY_SUCCESS:
      return { ...state, loading: false, activity: Array.isArray(action.payload) ? action.payload : [] };
    case GET_USER_ACTIVITY_FAIL:
      return { ...state, loading: false, error: action.payload };

    // Clear
    case CLEAR_ERRORS:
      return {
        ...state,
        error: null,
        message: null,
        isUpdated: false,
        isPasswordUpdated: false,
      };

    default:
      return state;
  }
};

// --- Admin/Franchise User Management state ---
const initialAdminState = {
  users: [],
  usersByFranchise: [],
  selectedUser: null,
  loading: false,
  error: null,
  message: null,
  isUpdated: false,
  isDeleted: false,
};

export const adminUserReducer = (state = initialAdminState, action) => {
  switch (action.type) {
    // All Users
    case ALL_USERS_REQUEST:
      return { ...state, loading: true };
    case ALL_USERS_SUCCESS:
      return { ...state, loading: false, users: Array.isArray(action.payload) ? action.payload : [] };
    case ALL_USERS_FAIL:
      return { ...state, loading: false, error: action.payload };

    // Users By Franchise
    case USERS_BY_FRANCHISE_REQUEST:
      return { ...state, loading: true };
    case USERS_BY_FRANCHISE_SUCCESS:
      return { ...state, loading: false, usersByFranchise: Array.isArray(action.payload) ? action.payload : [] };
    case USERS_BY_FRANCHISE_FAIL:
      return { ...state, loading: false, error: action.payload };

    // Create/Edit User
    case CREATE_USER_REQUEST:
    case EDIT_USER_REQUEST:
      return { ...state, loading: true, isUpdated: false, error: null, message: null };
    case CREATE_USER_SUCCESS:
      return { ...state, loading: false, isUpdated: true, message: "User created successfully." };
    case EDIT_USER_SUCCESS:
      return { ...state, loading: false, isUpdated: true, message: "User updated successfully." };
    case CREATE_USER_FAIL:
    case EDIT_USER_FAIL:
      return { ...state, loading: false, error: action.payload };

    // Delete User
    case DELETE_USER_REQUEST:
      return { ...state, loading: true, isDeleted: false, error: null };
    case DELETE_USER_SUCCESS:
      return { ...state, loading: false, isDeleted: true, message: action.payload?.message || "User deleted successfully." };
    case DELETE_USER_FAIL:
      return { ...state, loading: false, error: action.payload };

    // Change User Role
    case CHANGE_USER_ROLE_REQUEST:
      return { ...state, loading: true, isUpdated: false };
    case CHANGE_USER_ROLE_SUCCESS:
      return { ...state, loading: false, isUpdated: true, message: "User role updated successfully." };
    case CHANGE_USER_ROLE_FAIL:
      return { ...state, loading: false, error: action.payload };

    // Toggle User Status
    case TOGGLE_USER_STATUS_REQUEST:
      return { ...state, loading: true };
    case TOGGLE_USER_STATUS_SUCCESS:
      return { ...state, loading: false, isUpdated: true, message: "User status updated successfully." };
    case TOGGLE_USER_STATUS_FAIL:
      return { ...state, loading: false, error: action.payload };

    // Clear
    case CLEAR_ERRORS:
      return {
        ...state,
        error: null,
        message: null,
        isUpdated: false,
        isDeleted: false,
      };

    default:
      return state;
  }
};
